import React, { useState, useRef, useEffect } from 'react';
import { Calendar, Video, FileText, ClipboardList, CreditCard, Target, Users, BarChart2, Globe, Zap, PieChart, Clipboard } from 'lucide-react';

const TabIcon = ({ icon, title, isActive, onClick }) => {
  return (
    <div 
      className={`flex flex-col items-center p-3 md:p-5 cursor-pointer transition-all duration-300 ${
        isActive ? 'text-emerald-600' : 'text-gray-500 hover:text-gray-700'
      }`}
      onClick={onClick}
    >
      <div className="mb-2 md:mb-3">{React.cloneElement(icon, { className: 'w-8 h-8 md:w-10 md:h-10' })}</div>
      <div className={`text-center text-xs md:text-base font-medium ${isActive ? 'text-emerald-600' : ''}`}>{title}</div>
      {isActive && 
        <div className="mt-1 w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-emerald-600"></div>
      }
    </div>
  );
};

const TabbedFeaturesSection = () => {
  const [activeTab, setActiveTab] = useState('DASHBOARD');
  const tabsContainerRef = useRef(null);
  
  // Scroll the active tab into view when it changes
  useEffect(() => {
    if (tabsContainerRef.current) {
      const activeTabElement = tabsContainerRef.current.querySelector(`[data-tab="${activeTab}"]`);
      if (activeTabElement) {
        const containerWidth = tabsContainerRef.current.offsetWidth;
        const scrollPosition = activeTabElement.offsetLeft - (containerWidth / 2) + (activeTabElement.offsetWidth / 2);
        
        tabsContainerRef.current.scrollTo({
          left: Math.max(0, scrollPosition),
          behavior: 'smooth'
        });
      }
    }
  }, [activeTab]);
  
  const tabs = [
    { 
      id: 'DASHBOARD', 
      icon: <PieChart />,
      title: 'DASHBOARD',
      badge: 'INTUITIVE',
      description: 'Get a complete overview of your coaching business with our intuitive dashboard. Track client progress, revenue, and key metrics all in one place.',
      features: [
        'Client engagement analytics',
        'Revenue and financial insights',
        'Progress tracking metrics',
        'Business health indicators'
      ]
    },
    { 
      id: 'CLIENTS', 
      icon: <Users />, 
      title: 'CLIENT MANAGEMENT',
      badge: 'ESSENTIAL',
      description: 'Manage all your client relationships in one place with detailed profiles, messaging, and progress tracking to deliver personalized coaching experiences.',
      features: [
        'Comprehensive client profiles',
        'Secure messaging system',
        'Progress tracking and notes',
        'Client onboarding automation'
      ]
    },
    { 
      id: 'PAYMENTS', 
      icon: <CreditCard />, 
      title: 'PAYMENTS & PACKAGES',
      badge: 'ADVANCED',
      description: 'Set up flexible payment options and package offerings for your clients with seamless Stripe integration and automated billing.',
      features: [
        'Custom package creation',
        'Recurring subscription management',
        'Seamless payment processing',
        'Revenue tracking and reporting'
      ]
    },
    { 
      id: 'AUTOMATIONS', 
      icon: <Target />, 
      title: 'AUTOMATIONS',
      badge: 'EXCLUSIVE',
      description: 'Save time and ensure consistent client experiences with powerful automations for messaging, check-ins, and program delivery.',
      features: [
        'Pre-built automation templates',
        'Custom workflow creation',
        'Trigger-based actions',
        'Community template library'
      ]
    },
    { 
      id: 'PROGRAMS', 
      icon: <Clipboard />, 
      title: 'PROGRAM DESIGN',
      description: 'Create professional coaching programs with our intuitive builder. Design workouts, meal plans, and educational content for your clients.',
      features: [
        'Workout builder and library',
        'Program template creation',
        'Drag-and-drop interface',
        'Client-specific customization'
      ]
    },
    { 
      id: 'SCHEDULING', 
      icon: <Calendar />,
      title: 'SCHEDULING',
      badge: 'ADVANCED',
      description: 'Smart scheduling system that syncs with your calendar, sends automatic reminders, and reduces no-shows by 80%. Clients can easily book appointments without the back-and-forth.',
      features: [
        'Calendar sync with Google, Outlook, and Apple',
        'Automated reminders and follow-ups',
        'Custom availability settings',
        'Group and 1:1 session scheduling'
      ]
    },
    { 
      id: 'WEBSITE', 
      icon: <Globe />, 
      title: 'WEBSITE BUILDER',
      badge: 'EXCLUSIVE',
      description: 'Create a professional coaching website without any technical skills. Our drag-and-drop builder helps you showcase your services and attract new clients.',
      features: [
        'Customizable coaching templates',
        'Mobile-responsive design',
        'Built-in booking functionality',
        'SEO optimization tools'
      ]
    },
    { 
      id: 'VIDEO', 
      icon: <Video />, 
      title: 'VIDEO SESSIONS',
      description: 'Conduct seamless video coaching sessions with built-in tools designed specifically for coaching interactions and client engagement.',
      features: [
        'No downloads required for clients',
        'Screen sharing and collaborative tools',
        'Session recording with AI summary',
        'Whiteboard and annotation features'
      ]
    }
  ];
  
  const activeTabData = tabs.find(tab => tab.id === activeTab);
  
  return (
    <section id="features" className="pt-4 pb-16 bg-white w-full overflow-hidden">
      <div className="max-w-full mx-auto px-4 md:px-6">
        <div className="text-center mb-8 md:mb-12 max-w-5xl mx-auto">
          <div className="inline-block bg-emerald-50 text-emerald-700 px-4 py-2 md:px-5 md:py-3 rounded-full text-sm md:text-base font-medium mb-4 md:mb-5">
            All-in-One Platform
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-5">Everything You Need in One Place</h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-4xl mx-auto">
            Replace 5+ separate tools with our comprehensive coaching platform
          </p>
        </div>
        
        {/* Tabs - Scrollable container with visible indicators */}
        <div className="relative mb-8 md:mb-10">
          {/* Left fade indicator */}
          <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none"></div>
          
          {/* Tabs container */}
          <div 
            ref={tabsContainerRef}
            className="flex overflow-x-auto py-2 hide-scrollbar px-4 scroll-px-4"
          >
            <div className="flex min-w-max mx-auto">
              {tabs.map((tab) => (
                <div 
                  key={tab.id} 
                  data-tab={tab.id}
                >
                  <TabIcon 
                    icon={tab.icon}
                    title={tab.title}
                    isActive={activeTab === tab.id}
                    onClick={() => setActiveTab(tab.id)}
                  />
                </div>
              ))}
            </div>
          </div>
          
          {/* Right fade indicator */}
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none"></div>
        </div>
        
        {/* Content */}
        <div className="border border-gray-100 rounded-lg overflow-hidden mb-10 md:mb-14 max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row">
            {/* Left side - features */}
            <div className="p-6 md:p-8 lg:p-10 flex flex-col justify-center md:w-1/3">
              <div className="flex items-center flex-wrap mb-4 md:mb-6">
                <h3 className="text-2xl md:text-3xl font-bold mr-2">{activeTabData.title}</h3>
                {activeTabData.badge && (
                  <span className="mt-1 md:mt-0 bg-emerald-100 text-emerald-800 text-xs md:text-sm font-medium px-2.5 py-0.5 md:px-3 md:py-1 rounded">
                    {activeTabData.badge}
                  </span>
                )}
              </div>
              <p className="text-lg md:text-xl text-gray-700 mb-6 md:mb-8">
                {activeTabData.description}
              </p>
              <div className="space-y-4 md:space-y-5">
                {activeTabData.features.map((feature, index) => (
                  <div key={index} className="flex items-start">
                    <div className="text-emerald-600 text-lg md:text-xl mr-3 md:mr-4 mt-0.5">✓</div>
                    <p className="text-gray-700 text-lg md:text-xl">{feature}</p>
                  </div>
                ))}
              </div>
            </div>
            
        
           <div className="bg-gray-50 flex items-center justify-center relative md:w-2/3 h-[400px] md:h-[520px] lg:h-[585px] overflow-hidden">
              <div className="relative w-full h-full p-4 md:p-6">
                <div className="relative w-full h-full rounded-xl overflow-hidden border border-gray-200 shadow-md">
                  <video
                    id="featureVideo"
                    src="/demo.mov"
                    controls={false}
                    playsInline
                    muted
                    defaultMuted
                    onVolumeChange={(e) => e.target.muted = true}
                    className="w-full h-full object-contain"
                    title={`${activeTabData.title} Feature Video`}
                    ref={videoRef => {
                      if (videoRef) {
                        videoRef.addEventListener('play', function() {
                          const overlay = document.getElementById('videoOverlay');
                          if (overlay) overlay.style.display = 'none';
                          this.muted = true; // Ensure muted on play
                        });
                      }
                    }}
                  ></video>
                  
                  {/* Custom thumbnail overlay with play button */}
                  <div 
                    id="videoOverlay" 
                    className="absolute inset-0 bg-emerald-50/20 backdrop-blur-[1px] flex items-center justify-center cursor-pointer"
                    onClick={() => {
                      const video = document.getElementById('featureVideo');
                      if (video) {
                        // Custom controls that don't include volume
                        video.controls = false;
                        video.play();
                        video.muted = true; // Force muted state
                        document.getElementById('videoOverlay').style.display = 'none';
                      }
                    }}
                  >
                    <div className="w-24 h-24 md:w-32 lg:w-36 md:h-32 lg:h-36 bg-white/90 rounded-full flex items-center justify-center hover:opacity-90 transition-all duration-300">
                      <div className="w-12 h-12 md:w-16 lg:w-18 md:h-16 lg:h-18 flex items-center justify-center">
                        <svg viewBox="0 0 24 24" className="w-full h-full" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M8 5.14v14l11-7-11-7z" fill="#2D3748" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="text-center">
          <a 
            href="https://calendly.com/kolanut" 
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center bg-black text-white px-6 py-4 md:px-10 md:py-5 rounded-xl text-lg md:text-xl font-medium hover:bg-emerald-600 transition duration-300"
          >
            <span>See All Features in Action</span>
            <svg className="w-5 h-5 md:w-6 md:h-6 ml-2 md:ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
            </svg>
          </a>
        </div>
      </div>
      
      {/* Custom CSS for hiding scrollbar but allowing scroll */}
      <style jsx>{`
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
};

export default TabbedFeaturesSection;